import { useState, useEffect } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { quoteRequestsAPI } from '@/lib/api';
import { formatDate, getStatusColor } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  Loader2, 
  FileText, 
  Filter,
  Calendar,
  MapPin,
  Package,
  Mail,
  Phone,
  Building2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Zap,
  Sparkles,
  HardHat,
  PartyPopper,
  CalendarDays,
  Truck,
  Wrench
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui/radio-group';

// Calculate urgency based on start date
const getQuoteUrgency = (startDate) => {
  if (!startDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(startDate);
  const diffTime = targetDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return 'past'; // Already passed
  if (diffDays < 2) return 'rush'; // Under 48 hours
  if (diffDays < 7) return 'short'; // 2-7 days
  return 'normal';
};

export default function AdminQuoteRequests() {
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [updating, setUpdating] = useState(false);
  
  // Approval flow state
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [approveQuote, setApproveQuote] = useState(null);
  const [approvalContext, setApprovalContext] = useState('construction');
  const [approvalFrequency, setApprovalFrequency] = useState('');
  const [approving, setApproving] = useState(false);
  const [approvalResult, setApprovalResult] = useState(null);

  useEffect(() => {
    fetchQuotes();
  }, [statusFilter]);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const response = await quoteRequestsAPI.getAll(statusFilter !== 'all' ? statusFilter : undefined);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (quoteId, newStatus) => {
    setUpdating(true);
    try {
      await quoteRequestsAPI.update(quoteId, { status: newStatus });
      toast.success(`Quote marked as ${newStatus.replace('_', ' ')}`);
      fetchQuotes();
      setSelectedQuote(null);
    } catch (error) {
      console.error('Error updating quote:', error);
      toast.error('Failed to update quote');
    } finally {
      setUpdating(false);
    }
  };

  // Open approval dialog
  const openApprovalDialog = (quote) => {
    setApproveQuote(quote);
    // Pre-select context based on event_type
    if (quote.event_type === 'event') {
      setApprovalContext('event');
    } else if (quote.event_type === 'construction') {
      setApprovalContext('construction');
    } else {
      setApprovalContext('construction'); // default
    }
    setApprovalFrequency(''); // Let the system auto-determine
    setApprovalResult(null);
    setShowApproveDialog(true);
    setSelectedQuote(null);
  };

  // Handle quote approval
  const handleApproveQuote = async () => {
    if (!approveQuote) return;
    
    setApproving(true);
    try {
      const response = await quoteRequestsAPI.approve(approveQuote.id, {
        context: approvalContext,
        service_frequency: approvalFrequency || undefined // Let backend auto-determine if empty
      });
      
      setApprovalResult(response.data);
      toast.success(`Quote approved! ${response.data.total_jobs_created} jobs created.`, {
        duration: 5000,
        icon: '🎉'
      });
      fetchQuotes();
    } catch (error) {
      console.error('Error approving quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve quote');
    } finally {
      setApproving(false);
    }
  };

  // Calculate service frequency based on context
  const getDefaultFrequency = () => {
    if (!approveQuote) return '';
    const start = new Date(approveQuote.start_date);
    const end = new Date(approveQuote.end_date);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    
    if (approvalContext === 'construction') {
      return 'weekly';
    } else if (approvalContext === 'event') {
      return days <= 1 ? 'end_of_event' : 'daily';
    }
    return 'weekly';
  };

  const statusCounts = {
    new: quotes.filter(q => q.status === 'new').length,
    reviewed: quotes.filter(q => q.status === 'reviewed').length,
    converted: quotes.filter(q => q.status === 'converted_to_job').length,
    declined: quotes.filter(q => q.status === 'declined').length,
  };

  return (
    <AdminLayout title="Quote Requests">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card className={`border-slate-200 cursor-pointer hover:border-blue-300 ${statusFilter === 'new' ? 'border-blue-500 bg-blue-50' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'new' ? 'all' : 'new')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{statusCounts.new}</p>
            <p className="text-sm text-slate-500">New</p>
          </CardContent>
        </Card>
        <Card className={`border-slate-200 cursor-pointer hover:border-amber-300 ${statusFilter === 'reviewed' ? 'border-amber-500 bg-amber-50' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'reviewed' ? 'all' : 'reviewed')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{statusCounts.reviewed}</p>
            <p className="text-sm text-slate-500">Reviewed</p>
          </CardContent>
        </Card>
        <Card className={`border-slate-200 cursor-pointer hover:border-emerald-300 ${statusFilter === 'converted_to_job' ? 'border-emerald-500 bg-emerald-50' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'converted_to_job' ? 'all' : 'converted_to_job')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{statusCounts.converted}</p>
            <p className="text-sm text-slate-500">Converted</p>
          </CardContent>
        </Card>
        <Card className={`border-slate-200 cursor-pointer hover:border-red-300 ${statusFilter === 'declined' ? 'border-red-500 bg-red-50' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'declined' ? 'all' : 'declined')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{statusCounts.declined}</p>
            <p className="text-sm text-slate-500">Declined</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-slate-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="converted_to_job">Converted</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Quotes List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#194461]" />
        </div>
      ) : quotes.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <FileText className="mx-auto text-slate-300 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Quote Requests</h3>
            <p className="text-slate-600">
              {statusFilter !== 'all' ? 'Try changing the filter' : 'No quote requests yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {quotes.map((quote) => {
            const urgency = getQuoteUrgency(quote.start_date);
            const isUrgent = urgency === 'rush' || urgency === 'short';
            
            return (
            <Card 
              key={quote.id}
              className={`border-slate-200 hover:border-slate-300 transition-colors cursor-pointer ${
                urgency === 'rush' && quote.status === 'new' ? 'border-l-4 border-l-red-500 bg-red-50/30' : 
                urgency === 'short' && quote.status === 'new' ? 'border-l-4 border-l-amber-500 bg-amber-50/30' :
                quote.status === 'new' ? 'border-l-4 border-l-blue-500' : ''
              }`}
              onClick={() => setSelectedQuote(quote)}
              data-testid={`quote-card-${quote.id}`}
            >
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {/* Urgency Badge */}
                      {urgency === 'rush' && quote.status === 'new' && (
                        <Badge className="bg-red-500 text-white border-red-500 animate-pulse" data-testid={`rush-badge-${quote.id}`}>
                          <Zap size={12} className="mr-1" />
                          RUSH
                        </Badge>
                      )}
                      {urgency === 'short' && quote.status === 'new' && (
                        <Badge className="bg-amber-500 text-white border-amber-500" data-testid={`short-notice-badge-${quote.id}`}>
                          <Clock size={12} className="mr-1" />
                          SHORT NOTICE
                        </Badge>
                      )}
                      <Badge className={getStatusColor(quote.status)} variant="outline">
                        {quote.status.replace('_', ' ')}
                      </Badge>
                      <Badge variant="outline" className="bg-slate-50">
                        {quote.event_type}
                      </Badge>
                    </div>

                    <h3 className="font-semibold text-slate-900 mb-1">{quote.customer_name}</h3>
                    {quote.company_name && (
                      <p className="text-sm text-slate-600 flex items-center gap-1 mb-2">
                        <Building2 size={14} />
                        {quote.company_name}
                      </p>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600 mt-3">
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="text-slate-400" />
                        <span>{quote.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone size={14} className="text-slate-400" />
                        <span>{quote.phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin size={14} className="text-slate-400" />
                        <span>{quote.site_address}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-slate-400" />
                        <span>{formatDate(quote.start_date)} - {formatDate(quote.end_date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Package size={14} className="text-slate-400" />
                        <span>{quote.units_requested} {quote.unit_type} unit{quote.units_requested > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex sm:flex-col gap-2">
                    {quote.status === 'new' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateStatus(quote.id, 'reviewed');
                        }}
                        className="text-amber-600 border-amber-200 hover:bg-amber-50"
                      >
                        <Clock size={14} className="mr-1" />
                        Review
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          })}
        </div>
      )}

      {/* Quote Detail Dialog */}
      <Dialog open={!!selectedQuote} onOpenChange={() => setSelectedQuote(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Quote Request Details</DialogTitle>
            <DialogDescription>
              Submitted on {selectedQuote && formatDate(selectedQuote.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedQuote && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {/* Urgency Badge in Dialog */}
                {getQuoteUrgency(selectedQuote.start_date) === 'rush' && selectedQuote.status === 'new' && (
                  <Badge className="bg-red-500 text-white border-red-500">
                    <Zap size={12} className="mr-1" />
                    RUSH - Under 48 hours
                  </Badge>
                )}
                {getQuoteUrgency(selectedQuote.start_date) === 'short' && selectedQuote.status === 'new' && (
                  <Badge className="bg-amber-500 text-white border-amber-500">
                    <Clock size={12} className="mr-1" />
                    SHORT NOTICE - Under 7 days
                  </Badge>
                )}
                <Badge className={getStatusColor(selectedQuote.status)} variant="outline">
                  {selectedQuote.status.replace('_', ' ')}
                </Badge>
                <Badge variant="outline" className="bg-slate-50">
                  {selectedQuote.event_type}
                </Badge>
              </div>

              {/* Urgency Warning Box */}
              {getQuoteUrgency(selectedQuote.start_date) === 'rush' && selectedQuote.status === 'new' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                  <p className="text-sm text-red-700">
                    <strong>Rush request!</strong> Customer needs delivery within 48 hours. Call to confirm availability immediately.
                  </p>
                </div>
              )}

              <div className="space-y-3 text-sm">
                <div>
                  <p className="font-medium text-slate-900">{selectedQuote.customer_name}</p>
                  {selectedQuote.company_name && (
                    <p className="text-slate-600">{selectedQuote.company_name}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-slate-500 text-xs">Email</p>
                    <p className="text-slate-900">{selectedQuote.email}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Phone</p>
                    <p className="text-slate-900">{selectedQuote.phone}</p>
                  </div>
                </div>

                <div>
                  <p className="text-slate-500 text-xs">Site Address</p>
                  <p className="text-slate-900">{selectedQuote.site_address}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-slate-500 text-xs">Dates</p>
                    <p className="text-slate-900">
                      {formatDate(selectedQuote.start_date)} - {formatDate(selectedQuote.end_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Units</p>
                    <p className="text-slate-900">
                      {selectedQuote.units_requested} {selectedQuote.unit_type}
                    </p>
                  </div>
                </div>

                {selectedQuote.notes && (
                  <div>
                    <p className="text-slate-500 text-xs">Notes</p>
                    <p className="text-slate-900 bg-slate-50 p-2 rounded">{selectedQuote.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {selectedQuote?.status !== 'declined' && (
              <Button
                variant="outline"
                onClick={() => updateStatus(selectedQuote.id, 'declined')}
                disabled={updating}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <XCircle size={16} className="mr-2" />
                Decline
              </Button>
            )}
            {selectedQuote?.status !== 'reviewed' && selectedQuote?.status !== 'converted_to_job' && (
              <Button
                variant="outline"
                onClick={() => updateStatus(selectedQuote.id, 'reviewed')}
                disabled={updating}
                className="text-amber-600 border-amber-200 hover:bg-amber-50"
              >
                <Clock size={16} className="mr-2" />
                Mark Reviewed
              </Button>
            )}
            {selectedQuote?.status !== 'converted_to_job' && (
              <Button
                onClick={() => openApprovalDialog(selectedQuote)}
                disabled={updating}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="approve-quote-btn"
              >
                <Sparkles size={16} className="mr-2" />
                Approve & Create Jobs
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Quote Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={(open) => {
        if (!open) {
          setShowApproveDialog(false);
          setApproveQuote(null);
          setApprovalResult(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={20} className="text-emerald-500" />
              {approvalResult ? 'Jobs Created!' : 'Approve Quote & Create Jobs'}
            </DialogTitle>
            {!approvalResult && (
              <DialogDescription>
                This will automatically create delivery, service, and pickup jobs.
              </DialogDescription>
            )}
          </DialogHeader>

          {approvalResult ? (
            // Success state - show created jobs summary
            <div className="space-y-4 py-4">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-emerald-700 font-medium flex items-center gap-2">
                  <CheckCircle size={18} />
                  {approvalResult.message}
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <Truck size={18} className="text-blue-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">Delivery</p>
                    <p className="text-sm text-slate-600">{formatDate(approvalResult.delivery_job.scheduled_date)}</p>
                  </div>
                </div>

                {approvalResult.service_jobs.length > 0 && (
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Wrench size={18} className="text-amber-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{approvalResult.service_jobs.length} Service Job(s)</p>
                      <p className="text-sm text-slate-600">
                        {approvalResult.service_jobs.length === 1 
                          ? formatDate(approvalResult.service_jobs[0].scheduled_date)
                          : `${formatDate(approvalResult.service_jobs[0].scheduled_date)} - ${formatDate(approvalResult.service_jobs[approvalResult.service_jobs.length - 1].scheduled_date)}`
                        }
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <Package size={18} className="text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">Pickup</p>
                    <p className="text-sm text-slate-600">{formatDate(approvalResult.pickup_job.scheduled_date)}</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-slate-500 text-center">
                These jobs will appear in route recommendations for their scheduled dates.
              </p>
            </div>
          ) : (
            // Configuration state
            <div className="space-y-6 py-4">
              {approveQuote && (
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  <p className="font-medium text-slate-900">{approveQuote.customer_name}</p>
                  <p className="text-slate-600">{approveQuote.units_requested} {approveQuote.unit_type} unit(s)</p>
                  <p className="text-slate-600">{formatDate(approveQuote.start_date)} - {formatDate(approveQuote.end_date)}</p>
                </div>
              )}

              <div className="space-y-3">
                <Label>Job Type</Label>
                <RadioGroup 
                  value={approvalContext} 
                  onValueChange={setApprovalContext}
                  className="grid grid-cols-2 gap-3"
                >
                  <div className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 ${approvalContext === 'construction' ? 'border-[#52B8E1] bg-[#52B8E1]/5' : 'border-slate-200'}`}>
                    <RadioGroupItem value="construction" id="construction" />
                    <Label htmlFor="construction" className="cursor-pointer flex items-center gap-2 flex-1">
                      <HardHat size={18} className="text-amber-600" />
                      <div>
                        <p className="font-medium">Construction</p>
                        <p className="text-xs text-slate-500">Weekly service</p>
                      </div>
                    </Label>
                  </div>
                  <div className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 ${approvalContext === 'event' ? 'border-[#52B8E1] bg-[#52B8E1]/5' : 'border-slate-200'}`}>
                    <RadioGroupItem value="event" id="event" />
                    <Label htmlFor="event" className="cursor-pointer flex items-center gap-2 flex-1">
                      <PartyPopper size={18} className="text-purple-600" />
                      <div>
                        <p className="font-medium">Event</p>
                        <p className="text-xs text-slate-500">Daily/end-of-event</p>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label>Service Frequency</Label>
                <Select value={approvalFrequency || getDefaultFrequency()} onValueChange={setApprovalFrequency}>
                  <SelectTrigger data-testid="frequency-select">
                    <SelectValue placeholder="Auto-determine based on type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">
                      <div className="flex items-center gap-2">
                        <CalendarDays size={14} />
                        Weekly (every 7 days)
                      </div>
                    </SelectItem>
                    <SelectItem value="daily">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} />
                        Daily (every day)
                      </div>
                    </SelectItem>
                    <SelectItem value="end_of_event">
                      <div className="flex items-center gap-2">
                        <CheckCircle size={14} />
                        End of Event (once at end)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {approvalContext === 'construction' 
                    ? 'Construction sites typically need weekly servicing.'
                    : approveQuote && (() => {
                        const days = Math.ceil((new Date(approveQuote.end_date) - new Date(approveQuote.start_date)) / (1000 * 60 * 60 * 24));
                        return days <= 1 
                          ? 'Single-day event: service once after the event.' 
                          : `Multi-day event (${days} days): daily servicing recommended.`;
                      })()
                  }
                </p>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                <p className="font-medium mb-1">What will be created:</p>
                <ul className="list-disc list-inside space-y-0.5 text-blue-600">
                  <li>1 Delivery job on {approveQuote && formatDate(approveQuote.start_date)}</li>
                  <li>Service jobs ({(approvalFrequency || getDefaultFrequency()) === 'weekly' ? 'every week' : (approvalFrequency || getDefaultFrequency()) === 'daily' ? 'every day' : 'once at end'})</li>
                  <li>1 Pickup job on/after {approveQuote && formatDate(approveQuote.end_date)}</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            {approvalResult ? (
              <Button 
                onClick={() => {
                  setShowApproveDialog(false);
                  setApproveQuote(null);
                  setApprovalResult(null);
                }}
                className="w-full bg-[#194461] hover:bg-[#091D2E]"
              >
                Done
              </Button>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowApproveDialog(false);
                    setApproveQuote(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleApproveQuote}
                  disabled={approving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="confirm-approve-btn"
                >
                  {approving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles size={16} className="mr-2" />
                  )}
                  Approve & Create Jobs
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
